import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ChevronDown,
  ChevronUp,
  Settings,
  DollarSign,
  Shield,
  ExternalLink,
  Search,
  FileText,
  AlertCircle,
  CheckCircle2,
  Maximize2,
  Minimize2,
} from "lucide-react";

interface Scenario {
  id: string;
  title: string;
  summary: string;
  confidence: number;
  type: "technical" | "billing" | "security" | "general";
  tags: string[];
  wikiLink: string;
  steps?: string[];
  estimatedTime?: string;
}

const mockScenarios: Scenario[] = [
  {
    id: "1",
    title: "Password Reset for Enterprise Users",
    summary:
      "Standard procedure for resetting passwords in enterprise accounts with SSO integration",
    confidence: 94,
    type: "security",
    tags: ["Password", "Enterprise", "SSO"],
    wikiLink: "https://example.com",
    steps: [
      "Verify user identity using security questions",
      "Check if SSO is enabled for the account",
      "Generate temporary password via admin panel",
      "Send secure reset link to verified email",
      "Follow up within 24 hours",
    ],
    estimatedTime: "5-10 minutes",
  },
  {
    id: "2",
    title: "Billing Dispute Resolution",
    summary:
      "Process for handling billing disputes and refund requests under $500",
    confidence: 87,
    type: "billing",
    tags: ["Refund", "Billing", "Dispute"],
    wikiLink: "https://example.com",
    steps: [
      "Review transaction history",
      "Verify dispute validity",
      "Check refund eligibility",
      "Process refund if approved",
      "Update customer record",
    ],
    estimatedTime: "10-15 minutes",
  },
  {
    id: "3",
    title: "API Integration Troubleshooting",
    summary: "Common API connection issues and debugging steps for developers",
    confidence: 76,
    type: "technical",
    tags: ["API", "Integration", "Developer"],
    wikiLink: "https://example.com",
    steps: [
      "Check API key validity",
      "Verify endpoint URLs",
      "Review rate limiting",
      "Test with Postman/curl",
      "Escalate to engineering if needed",
    ],
    estimatedTime: "15-20 minutes",
  },
];

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 90) return "text-green-600 bg-green-50";
  if (confidence >= 75) return "text-yellow-600 bg-yellow-50";
  return "text-red-600 bg-red-50";
};

const getConfidenceIcon = (confidence: number) => {
  if (confidence >= 90) return <CheckCircle2 className="h-3 w-3" />;
  return <AlertCircle className="h-3 w-3" />;
};

export function SuggestedScenarios() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const toggleExpanded = (scenarioId: string) => {
    setExpandedScenario(expandedScenario === scenarioId ? null : scenarioId);
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <CardTitle className=" text-md">Suggested Scenarios</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {mockScenarios.length} matches
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-8 w-8 p-0"
          >
            {isCollapsed ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="space-y-4">
          {mockScenarios.map((scenario, index) => (
            <div key={scenario.id}>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm leading-tight">
                          {scenario.title}
                        </h4>
                        <div
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(
                            scenario.confidence
                          )}`}
                        >
                          {getConfidenceIcon(scenario.confidence)}
                          {scenario.confidence}% match
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {scenario.summary}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-7">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleExpanded(scenario.id)}
                    className="h-8 text-xs"
                  >
                    {expandedScenario === scenario.id
                      ? "Hide Details"
                      : "View Steps"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    asChild
                    disabled
                  >
                    <a
                      href={scenario.wikiLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Wiki
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </div>

                {expandedScenario === scenario.id && scenario.steps && (
                  <div className="ml-7 mt-3 p-3 bg-muted/50 rounded-lg">
                    <h5 className="font-medium text-sm mb-2">Action Steps:</h5>
                    <ol className="space-y-1 text-sm text-muted-foreground">
                      {scenario.steps.map((step, stepIndex) => (
                        <li key={stepIndex} className="flex gap-2">
                          <span className="font-medium text-primary">
                            {stepIndex + 1}.
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
              {index < mockScenarios.length - 1 && (
                <Separator className="mt-4" />
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}
